import {
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BACKEND_API_TYPE_FIELD_PATH } from '@app/shared/constants/api';

import { lazyInject } from '@app/shared/decorators/lazy-inject.decorator';
import { Observable, observableThrowError, rx } from '@app/shared/rx';
import {
  AnyObject,
  BackendApiType,
  EHeaderName,
  IApiListResponse,
} from '@app/shared/types';
import { IFilterQuery } from '@app/shared/types/common/filter-query.type';
import {
  IBaseEntityType,
  IDocumentOperationOptions,
  IErrorOverride,
} from '@app/shared/types/entities/base.type';
import { ObjectId } from '@app/shared/types/entities/entity-id.type';
import { getStartOfLocalDayISOString } from '@app/shared/utils/date-utils';
import { checkUrlStartSlash } from '@app/shared/utils/path-utils';
import { MAX_PAGE_SIZE } from '@config/global-constants';
import { isEmpty, forEach, get, merge } from 'lodash';
import { TokenSessionService } from '../session';
import { HttpErrorHandler } from './http-error-handler';
import { XEAHttpUrlEncodingCodec } from './http-url-encoding-codec';
import { IHttpOptions } from '@app/shared/types/common/common.type';
import { I18nService } from '../i18n/i18n.service';
import { ServiceWithCustomHttp } from './service-with-custom-http.service';

@Injectable()
abstract class BaseService extends ServiceWithCustomHttp {
  @lazyInject(TokenSessionService)
  public tokenSessionService: TokenSessionService;
  @lazyInject(I18nService)
  public i18nService: I18nService;

  public defaultQuery: IFilterQuery = {};

  protected catchHttpError =
    (errorOverride?: IErrorOverride) =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError((httpError: HttpErrorResponse) =>
          observableThrowError(
            HttpErrorHandler.composeError(httpError, {
              errorOverride,
              i18nService: this.i18nService,
            }),
          ),
        ),
      );

  /**
   * Method which creates HttpParams and returns
   */
  public createParams({
    filter,
    select: selectArg,
    top,
    skip,
    orderby,
    expand,
    effectiveDate,
    include_entities,
    include_nested_type,
    customParams,
    include_items,
    include_calculated_values,
    // tslint:disable-next-line:trailing-comma
    ...queryParams
  }: IFilterQuery = {}): HttpParams {
    let params = new HttpParams({ encoder: new XEAHttpUrlEncodingCodec() });

    // add fixed BACKEND_API_TYPE_FIELD_PATH params, will remove by url.interceptor
    params = params.set(
      BACKEND_API_TYPE_FIELD_PATH,
      this.config.backendApiType ?? BackendApiType.XEA,
    );

    // TODO find better solution for handling params
    if (filter) {
      params = params.set('$filter', filter);
    }

    if (!isEmpty(selectArg)) {
      params = params.set('$select', selectArg.toString());
    }
    if (top) {
      params = params.set('$top', top.toString());
    }
    if (skip) {
      params = params.set('$skip', skip.toString());
    }
    if (orderby) {
      params = params.set('$orderby', orderby.toString());
    }
    forEach(queryParams, (param, key) => {
      params = params.set(key, param.toString());
    });
    if (include_entities) {
      const entityList: string = include_entities
        .map((entityType) => `'${entityType}'`)
        .join(',');
      params = params.set('include_entities', `[${entityList}]`);
    }
    if (include_nested_type) {
      const typeList: string = include_nested_type
        .map((type) => `'${type}'`)
        .join(',');
      params = params.set('include_nested_type', `[${typeList}]`);
    }
    if (customParams) {
      customParams.map(({ key, value }) => {
        // tslint:disable-line:no-ignored-return
        params = params.set(key, String(value));
      });
    }
    if (expand) {
      params = params.set('$expand', expand.toString());
    }
    if (effectiveDate) {
      params = params.set('effective_date', effectiveDate);
    }
    if (include_items) {
      params = params.set('$include_items', include_items.toString());
    }
    if (include_calculated_values) {
      params = params.set(
        '$include_calculated_values',
        include_calculated_values.toString(),
      );
    }

    return params;
  }

  public composeHeadersForUpdateRequest(
    options: IDocumentOperationOptions = {},
    siteIds: ObjectId[] = [],
  ): HttpHeaders {
    const headersOptions = get(options, 'headers', {});
    if (!isEmpty(siteIds)) {
      headersOptions[EHeaderName.XSiteIds] = siteIds.join();
    }

    return new HttpHeaders(headersOptions);
  }

  /**
   * get list base url
   * @returns list base url
   */
  public getListBaseUrl(): string {
    const trailRoute = get(this.config, 'customEntityRoute.list', '');

    return trailRoute
      ? `/${this.config.apiEndpoint}/${checkUrlStartSlash(trailRoute)}`
      : `/${this.config.apiEndpoint}`;
  }

  /**
   * Method for getting document by ID
   *
   * @param id document ID
   * @param options
   */
  public getDocumentById(
    id: string,
    options: IDocumentOperationOptions = {},
  ): Observable<IBaseEntityType> {
    const params: HttpParams = this.createParams(options.query);
    const headers = this.composeHeadersForUpdateRequest({
      ...options,
      disallowBusinessDateHeaders: true,
    });

    return this.http
      .get(`/${this.config.apiEndpoint}/${id}`, { params, headers })
      .pipe(
        this.catchHttpError(options.errorOverride),
        rx.map((item) => this.backToFrontItemMapper(item)),
      );
  }

  /**
   * Method for creating new document
   *
   * @param document entity document object
   * @param options
   */
  public createDocument(document, options: IHttpOptions = {}): Observable<any> {
    const body = this.frontToBackItemMapper(document);

    return this.http.post(`/${this.config.apiEndpoint}`, body, options).pipe(
      this.catchHttpError(),
      rx.map((item) => this.backToFrontItemMapper(item)),
    );
  }

  public updateDocument<T>(
    document: T,
    id: string = null,
    options: IDocumentOperationOptions = {},
  ): Observable<IBaseEntityType> {
    const headers = this.composeHeadersForUpdateRequest({
      ...options,
      disallowBusinessDateHeaders: true,
    });

    const params: HttpParams = this.createParams();

    return this.http
      .patch(
        `${this.config.apiEndpoint}${isEmpty(id) ? '' : `/${id}`}`,
        document,
        { params, headers },
      )
      .pipe(this.catchHttpError(options.errorOverride));
  }

  public prepareDataForRequest<T = IBaseEntityType>(
    data: T[] | T,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options: IDocumentOperationOptions,
  ): T[] | T {
    const mappedDoc = this.documentForRequestHandler(data);

    return mappedDoc;
  }

  public documentForRequestHandler<T = IBaseEntityType>(document: T): T {
    return this.frontToBackItemMapper(document);
  }

  public frontToBackItemMapper<T = IBaseEntityType>(doc: T): T {
    return doc;
  }

  public fetchList<T = IBaseEntityType>(
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions = {},
  ): Observable<IApiListResponse<T>> {
    const headers = this.composeHeadersForUpdateRequest({
      ...options,
      disallowBusinessDateHeaders: true,
    });
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const params: HttpParams = this.createParams({
      top: options?.withoutTop ? null : MAX_PAGE_SIZE,
      ...query,
    });

    return this.http.get(this.getListBaseUrl(), { params, headers }).pipe(
      this.catchHttpError(options.errorOverride),
      rx.map((res) =>
        options.skipMappers ? res : this.backToFrontMapper(res),
      ),
    );
  }

  public fetchCurrentList(
    query?: IFilterQuery,
    siteIds: ObjectId[] = [],
    options: IDocumentOperationOptions = {},
  ): Observable<IApiListResponse> {
    let params: HttpParams = this.createParams({
      top: MAX_PAGE_SIZE,
      ...query,
    });
    params = params.set(
      'effective_date',
      get(query, 'effectiveDate', getStartOfLocalDayISOString()),
    );
    params = params.set(
      'include_mappings',
      get(query, 'include_mappings', 'true') as string,
    );

    const headers = this.composeHeadersForUpdateRequest(options, siteIds);

    return this.http
      .get<AnyObject>(`/${this.config.apiEndpoint}/current`, {
        params,
        headers,
      })
      .pipe(
        this.catchHttpError(options.errorOverride),
        rx.map((response) => {
          if (Array.isArray(response.items)) {
            return response;
          }
          throw new Error(response.errorMessage);
        }),
        rx.map((res) =>
          options.skipMappers ? res : this.backToFrontMapper(res),
        ),
      );
  }

  public backToFrontItemMapper(doc: IBaseEntityType): IBaseEntityType {
    return doc;
  }

  /**
   * response mapper
   */
  public backToFrontMapper(res): any {
    return Object.assign(res, {
      items: res.items.map((item) => this.backToFrontItemMapper(item)),
    });
  }

  public acknowledgeDocument(id: string): Promise<any> {
    return this.http
      .patch(`/${this.config.apiEndpoint}/${id}/acknowledge`, null)
      .pipe(this.catchHttpError())
      .toPromise();
  }
}

export { BaseService };
